import {Trans} from '@lingui/macro';
import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {ReferenceFormatModel} from '../../../../models/api/autolinkerModel';
import {isStringOrTemplate} from '../../../editors/commons/stringTemplateModel';
import {getParameters} from '../../../editors/expressions/expressionInferHelpers';
import {OccurrencesEnum} from '../../../editors/expressions/infer/stringMethod';
import {RuleMultilineParameterCard} from '../ruleMultilineParameterCard';
import type {AtomCardProps} from './atomCard';
import {AtomIcon} from './atomIcon';
import {AtomTitle} from './atomTitle';
import {StyledParameterLineDiv, StyledParameterTitleLineDiv} from './parameterLine';
import {AtomParameterPicker} from './pickers/atomParameterPicker';
import {RegexPlayground} from './regexPlayground';

/*
 * Styles.
 */

const PlaygroundWrapper = styled.div`
  display: flex;
  margin-top: 10px;
  justify-content: center;
`;

/*
 * Component.
 */

export const ExtractTextAtomCard: FC<AtomCardProps> = (props) => {
  const {atom, isDisabled, onUpdateAtom, onCancel} = props;
  const {params} = atom;
  const [occurrenceParam, patternParam, beforeParam, afterParam] = getParameters(atom);

  const shouldExpand = Boolean(atom.params.before);

  const collapsableParameters = useMemo(() => {
    const beforeNode = (
      <StyledParameterLineDiv key="before">
        <StyledParameterTitleLineDiv>
          <Trans id="rule-expressions-preceded">Only if preceded by</Trans>
        </StyledParameterTitleLineDiv>
        <AtomParameterPicker
          parameter={beforeParam}
          atom={atom}
          isDisabled={isDisabled}
          onUpdate={onUpdateAtom}
        />
      </StyledParameterLineDiv>
    );

    const afterNode = (
      <StyledParameterLineDiv key="after">
        <StyledParameterTitleLineDiv>
          <Trans id="rule-expressions-followed">Only if followed by</Trans>
        </StyledParameterTitleLineDiv>
        <AtomParameterPicker
          parameter={afterParam}
          atom={atom}
          isDisabled={isDisabled}
          onUpdate={onUpdateAtom}
        />
      </StyledParameterLineDiv>
    );

    return [beforeNode, afterNode];
  }, [beforeParam, afterParam, atom, isDisabled, onUpdateAtom]);

  const title = (
    <AtomTitle atom={atom} title={<Trans id="rule-expressions-extract">Extract</Trans>}>
      <AtomParameterPicker
        parameter={occurrenceParam}
        atom={atom}
        onUpdate={onUpdateAtom}
        isDisabled={isDisabled}
      />
      <span>
        <Trans id="rule-expressions-matching">matching</Trans>
      </span>
    </AtomTitle>
  );

  const format: ReferenceFormatModel = useMemo(() => {
    const pattern = isStringOrTemplate(params.pattern) ? params.pattern : '';
    const before = isStringOrTemplate(params.before) ? params.before : undefined;
    const after = isStringOrTemplate(params.after) ? params.after : undefined;

    return {pattern, before, after};
  }, [params]);

  const shouldOnlyMatchFirst = params.occurrences === OccurrencesEnum.FIRST;

  return (
    <RuleMultilineParameterCard
      errorMessage={atom.errorMessage}
      icon={<AtomIcon atom={atom} />}
      title={title}
      onCancel={onCancel}
      collapsableParameters={collapsableParameters}
      shouldExpandCollapsableParameters={shouldExpand}
      footer={
        <PlaygroundWrapper>
          <RegexPlayground
            format={format}
            shouldOnlyMatchFirst={shouldOnlyMatchFirst}
            title={<Trans id="extract-text-test">Test extract text</Trans>}
            description={
              <Trans id="extract-text-help">Highlighted text will be extracted by this step</Trans>
            }
          />
        </PlaygroundWrapper>
      }
    >
      <AtomParameterPicker
        parameter={patternParam}
        atom={atom}
        isDisabled={isDisabled}
        onUpdate={onUpdateAtom}
      />
    </RuleMultilineParameterCard>
  );
};
